import React, { useState, useEffect } from "react";
import { Plus, MapPin, Trash2, Pencil, Upload } from "lucide-react";
import {
  useGetAcademyQuery,
  usePostAcademyMutation,
  useUpdateAcademyMutation,
  useDeleteAcademyMutation,
  useGetUsersQuery,
  useGetDepartmentQuery,
  useGetDivisionsQuery,
} from "./../../slices/userApiSlice";
import { useGetAllVendorsQuery } from "./../../slices/vendorApiSlice";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Card, CardContent } from "../../components/ui/card";

const SAdminAcademies = () => {
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [academyToDelete, setAcademyToDelete] = useState(null);

  const [newAcademy, setNewAcademy] = useState({
    name: "",
    location: "",
    description: "",
    image: "",
    imagePreview: "",
  });

  const [currentAcademy, setCurrentAcademy] = useState({
    name: "",
    location: "",
    description: "",
    image: "",
  });

  const { data: academies, isLoading, error, refetch } = useGetAcademyQuery();
  const [addAcademy, { isLoading: isAddingLoading }] = usePostAcademyMutation();
  const [updateAcademy, { isLoading: isSavingLoading }] =
    useUpdateAcademyMutation();
  const [deleteAcademy] = useDeleteAcademyMutation();

  // Fetch all data to check dependencies
  const { data: allUsers } = useGetUsersQuery();
  const { data: allDepartments } = useGetDepartmentQuery();
  const { data: allDivisions } = useGetDivisionsQuery();
  const { data: allVendors } = useGetAllVendorsQuery();

  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading academies...");
    } else {
      toast.dismiss();
    }
    return () => toast.dismiss();
  }, [isLoading]);

  useEffect(() => {
    if (error) {
      toast.error("Error Fetching Data", {
        description:
          "Unable to retrieve academies at this time. Please try again later.",
      });
    }
  }, [error]);

  const handleImageUpload = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      if (isEdit) {
        setCurrentAcademy((prev) => ({
          ...prev,
          image: file,
          imagePreview: preview,
        }));
      } else {
        setNewAcademy((prev) => ({
          ...prev,
          image: file,
          imagePreview: preview,
        }));
      }
    }
  };

  const handleAdd = async () => {
    const { name, location, description, image } = newAcademy;

    if (!name.trim() || !location.trim() || !description.trim()) {
      toast.error("Please fill in all fields", {
        description: "Name, Location, and Description are required.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("location", location.trim());
    formData.append("description", description.trim());

    if (image) {
      formData.append("image", image);
    }

    try {
      await addAcademy(formData).unwrap();

      toast.success("Success", {
        description: "Academy successfully added!",
      });

      setShowModal(false);
      setNewAcademy({
        name: "",
        location: "",
        description: "",
        image: "",
        imagePreview: "",
      });

      refetch();
    } catch (err) {
      toast.error("Failed to add academy", {
        description: err?.data?.message || "Something went wrong.",
      });
    }
  };

  const handleEdit = async () => {
    try {
      const { academyId, name, location, description, image } = currentAcademy;

      await updateAcademy({
        academyId,
        name,
        location,
        description,
        image,
      }).unwrap();

      toast.success("Success", {
        description: "Academy updated successfully!",
      });

      setShowEditModal(false);
      refetch();
    } catch (err) {
      toast.error("Failed to update academy", {
        description:
          err?.data?.message || err?.error || "Something went wrong.",
      });
    }
  };

  const handleDelete = async (id, academyName) => {
    if (!id) {
      return;
    }

    const users = allUsers?.filter((user) => user.academyId === id) || [];
    const vendors =
      allVendors?.filter(
        (vendor) => vendor.academyId === id || vendor.academyID === id,
      ) || [];

    const dependencies = [];
    if (users.length > 0) {
      dependencies.push(`${users.length} user${users.length > 1 ? "s" : ""}`);
    }
    if (vendors.length > 0) {
      dependencies.push(
        `${vendors.length} vendor${vendors.length > 1 ? "s" : ""}`,
      );
    }

    if (dependencies.length > 0) {
      const dependencyList = dependencies.join(", ");
      toast.error("Cannot Delete Academy", {
        description: `This academy cannot be deleted because it has ${dependencyList}. Please delete all ${dependencyList} first before deleting this academy.`,
      });
      return;
    }

    setAcademyToDelete({ id, name: academyName });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!academyToDelete?.id) return;

    try {
      await deleteAcademy(academyToDelete.id).unwrap();
      refetch();
      toast.success("Academy deleted successfully.");
      setDeleteDialogOpen(false);
      setAcademyToDelete(null);
    } catch (err) {
      const errorMessage =
        err?.data?.message || err.error || "Something went wrong.";

      if (
        errorMessage.toLowerCase().includes("user") ||
        errorMessage.toLowerCase().includes("vendor") ||
        errorMessage.toLowerCase().includes("department") ||
        errorMessage.toLowerCase().includes("division") ||
        errorMessage.toLowerCase().includes("dependency")
      ) {
        toast.error("Cannot Delete Academy", {
          description: errorMessage,
        });
      } else {
        toast.error("Error!", {
          description: errorMessage,
        });
      }
    }
  };

  return (
    <div className="p-4 sm:py-6 sm:px-8 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Academies
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage and organize academy information across the system
            </p>
          </div>
          <Button onClick={() => setShowModal(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Academy
          </Button>
        </div>
      </div>

      {/* Academies Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {error ? (
          <div className="col-span-full">
            <Card className="border-destructive">
              <CardContent className="pt-6">
                <p className="text-destructive text-center">
                  Error fetching academies
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          academies?.map((academy) => (
            <Card
              key={academy.academyId}
              className="group overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={academy.image}
                  alt={academy.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

                {/* Overlay Actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                  <Button
                    size="icon"
                    variant="secondary"
                    className="h-10 w-10"
                    onClick={() => {
                      setCurrentAcademy({
                        academyId: academy.academyId,
                        name: academy.name,
                        location: academy.location,
                        description: academy.description,
                        image: academy.image,
                        imagePreview: academy.image,
                      });
                      setShowEditModal(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="destructive"
                    className="h-10 w-10"
                    onClick={() =>
                      handleDelete(academy.academyId, academy.name)
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {/* Academy Info */}
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <h3 className="font-semibold text-lg mb-1">{academy.name}</h3>
                  <div className="flex items-center gap-1 text-sm text-white/90">
                    <MapPin className="h-3 w-3" />
                    <span>{academy.location}</span>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Add Academy Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Academy</DialogTitle>
            <DialogDescription>
              Create a new academy by filling in the details below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Academy Name</Label>
              <Input
                id="name"
                placeholder="Enter academy name"
                value={newAcademy.name}
                onChange={(e) =>
                  setNewAcademy({
                    ...newAcademy,
                    name: e.target.value.trimStart(),
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                placeholder="Enter location"
                value={newAcademy.location}
                onChange={(e) =>
                  setNewAcademy({
                    ...newAcademy,
                    location: e.target.value.trimStart(),
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter academy description"
                value={newAcademy.description}
                onChange={(e) =>
                  setNewAcademy({
                    ...newAcademy,
                    description: e.target.value.trimStart(),
                  })
                }
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Academy Image</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, false)}
                  className="hidden"
                  id="image-upload"
                />
                <label htmlFor="image-upload" className="flex-1 cursor-pointer">
                  {newAcademy.imagePreview ? (
                    <div className="relative w-full h-32 rounded-md overflow-hidden border">
                      <img
                        src={newAcademy.imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <Upload className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-md hover:border-primary transition-colors">
                      <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Click to upload image
                      </p>
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowModal(false);
                setNewAcademy({
                  name: "",
                  location: "",
                  description: "",
                  image: "",
                  imagePreview: "",
                });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={
                !newAcademy.name ||
                !newAcademy.location ||
                !newAcademy.description ||
                isAddingLoading
              }
            >
              {isAddingLoading ? "Adding..." : "Add Academy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Academy Dialog */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Academy</DialogTitle>
            <DialogDescription>
              Update the academy information below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Academy Name</Label>
              <Input
                id="edit-name"
                value={currentAcademy.name}
                onChange={(e) =>
                  setCurrentAcademy({ ...currentAcademy, name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-location">Location</Label>
              <Input
                id="edit-location"
                value={currentAcademy.location}
                onChange={(e) =>
                  setCurrentAcademy({
                    ...currentAcademy,
                    location: e.target.value,
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={currentAcademy.description}
                onChange={(e) =>
                  setCurrentAcademy({
                    ...currentAcademy,
                    description: e.target.value,
                  })
                }
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Academy Image</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, true)}
                  className="hidden"
                  id="edit-image-upload"
                />
                <label
                  htmlFor="edit-image-upload"
                  className="flex-1 cursor-pointer"
                >
                  <div className="relative w-full h-32 rounded-md overflow-hidden border">
                    <img
                      src={currentAcademy.imagePreview || currentAcademy.image}
                      alt="Current"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <Upload className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={isSavingLoading}>
              {isSavingLoading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the academy "{academyToDelete?.name}
              ". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setAcademyToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Toaster />
    </div>
  );
};

export default SAdminAcademies;
